import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';

/**
 * Props for ErrorPage component
 */
interface ErrorPageProps {
  /** The error that occurred (optional for generic display) */
  error?: Error | null;
  /** HTTP status code (defaults to 500) */
  statusCode?: number;
  /** Custom error message to display */
  message?: string;
  /** Callback when user clicks retry */
  onRetry?: () => void;
}

/**
 * ErrorPage Component
 * 
 * Generic error page for handling application errors (500, server errors, etc.)
 * Provides a user-friendly error message with recovery options.
 * 
 * Features:
 * - Friendly error message
 * - Multiple recovery options (home, retry, back)
 * - Playful design matching app aesthetic
 * - Development mode shows error details
 * 
 * @validates Requirements 1.4, 1.7, 4.5, 5.10
 */
export function ErrorPage({ 
  error, 
  statusCode = 500, 
  message,
  onRetry 
}: ErrorPageProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isDevelopment = import.meta.env.DEV;

  // Get the translation key for the status code
  const getStatusCodeKey = (code: number): string => {
    const validCodes = [400, 401, 403, 404, 408, 429, 500, 502, 503, 504];
    return validCodes.includes(code) ? String(code) : 'default';
  };

  const statusKey = getStatusCodeKey(statusCode);

  // Determine the display message
  const displayMessage = message || t(`errors.generic.messages.${statusKey}`);
  const displayTitle = t(`errors.generic.titles.${statusKey}`);

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      // Default: reload the page
      window.location.reload();
    }
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="text-center space-y-6 px-4 max-w-lg mx-auto">
        {/* Error illustration */}
        <div className="text-8xl">
          {statusCode === 503 ? '🔧' : '😵'}
        </div>
        
        {/* Error code and title */}
        <div className="space-y-2">
          <h1 className="font-display text-6xl font-bold text-slate-900">
            {statusCode}
          </h1>
          <h2 className="text-xl font-semibold text-slate-700">
            {displayTitle}
          </h2>
          <p className="text-slate-600">
            {displayMessage}
          </p>
        </div>
        
        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
          <button
            onClick={handleRetry}
            className="btn-primary"
          >
            {t('errors.generic.retry')}
          </button>
          <Link
            to="/"
            className="btn-outline"
          >
            {t('errors.generic.backHome')}
          </Link>
          <button
            onClick={handleGoBack}
            className="btn-ghost"
          >
            {t('errors.generic.previousPage')}
          </button>
        </div>

        {/* Error details in development mode */}
        {isDevelopment && error && (
          <div className="mt-8 text-left bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-red-800 mb-2">
              {t('errors.generic.devDetails')}
            </h3>
            <pre className="text-xs text-red-700 overflow-auto max-h-40">
              {error.message}
              {error.stack && (
                <>
                  {'\n\n'}
                  {error.stack}
                </>
              )}
            </pre>
          </div>
        )}
        
        {/* Helpful message */}
        <p className="text-sm text-slate-400 pt-4">
          {t('errors.generic.persistMessage')}
        </p>
      </div>
    </div>
  );
}


